import React from "react";

import { Trans, useTranslation } from "react-i18next";

import { Alert, Collapse, Empty, List, Tag, Typography } from "antd";
import Paragraph from "antd/es/typography/Paragraph";

import { GateTesterResult } from "../../../../store/gate_tester/types";

import styles from "./styles.module.scss";

const { Text } = Typography;

export interface GateTesterResultsProps {
  tab: "flights" | "bookings";
  result?: GateTesterResult;
}

export interface ErrorCardProps {
  field: string;
  message: string;
  count: number;
  examples: string[];
}

export const ValidationError: React.FC<ErrorCardProps> = (props) => {
  const { t } = useTranslation("gate_tester_page");
  const { field, message, count, examples } = props;

  const errorExamples = examples.map((example, idx) => (
    <Typography.Text key={`example-${idx}`} code>
      {example}
    </Typography.Text>
  ));

  const description = (
    <>
      <p className={styles.errorDescription}>{message}</p>
      {errorExamples && (
        <Collapse>
          <Collapse.Panel key={1} header={t("page.results.error.examples")}>
            {errorExamples}
          </Collapse.Panel>
        </Collapse>
      )}
    </>
  );

  return (
    <Alert
      className={styles.error}
      message={
        <span>
          <Trans t={t} i18nKey={"page.results.error.title"} tOptions={{ field }}>
            Field <strong>{field}</strong> is invalid
          </Trans>
        </span>
      }
      description={description}
      showIcon
      icon={<Tag color="#f50">{count}</Tag>}
      type="error"
    />
  );
};

export const GateTesterResults: React.FC<GateTesterResultsProps> = (props) => {
  const { t } = useTranslation("gate_tester_page");

  const { tab, result } = props;

  return (
    <List className={styles.results}>
      {!result && <Empty description={": ("} />}
      {result && result.fetcherError && (
        <Alert
          type="error"
          className={styles.error}
          message={t("page.results.error.message")}
          description={
            <>
              <p>{t(`page.results.error.types.${result.fetcherError.type}`)}</p>
              <Collapse>
                <Collapse.Panel key={1} header={t("page.results.error.showErrorCollapse")}>
                  <Paragraph>
                    <pre style={{ overflowWrap: "anywhere" }}>{result.fetcherError.message}</pre>
                  </Paragraph>
                </Collapse.Panel>
              </Collapse>
            </>
          }
          showIcon
        />
      )}
      {result && !result.fetcherError && (
        <Alert
          type="info"
          message={""}
          description={
            <>
              <p>
                {t("page.results.statusCode")}: <Text code>{result.fetcherResult.statusCode}</Text>
              </p>
              <p>
                {t("page.results.contentType")}: <Text code>{result.fetcherResult.contentType}</Text>
              </p>
              <p>
                {t(`page.results.countObjects.${props.tab}`)}: <Text code>{result.objectsCount}</Text>
              </p>
            </>
          }
        />
      )}
      {result && !result.fetcherError && result.validationErrors.length === 0 && result.objectsCount > 0 && (
        <Alert
          message={t("page.results.passed.message")}
          description={t("page.results.passed.description")}
          type="success"
          showIcon
        />
      )}
      {result && !result.fetcherError && result.validationErrors.length === 0 && result.objectsCount === 0 && (
        <Alert
          message={t("page.results.empty.message")}
          description={t(`page.results.empty.description.${tab}`)}
          type="warning"
          showIcon
        />
      )}
      {props.result?.validationErrors.map((error, idx) => (
        <ValidationError key={`validation-error-${idx}`} {...error} />
      ))}
    </List>
  );
};
